import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import { logger } from '@/utils/logger';
import { pushDebugError } from '@/utils/debugErrorLog';

export interface BeemError {
  formattedString: string;
  details?: string;
  originalError: unknown;
  // Optional stable, machine-readable classification of the error, extracted
  // from the RAW error message (not the formatted/localized string). Set by
  // callers that recognize a known sentinel code thrown by the backend, so UI
  // can branch on this instead of parsing the display string.
  code?: string;
}

// Extracts the raw, un-formatted error message from a thrown value. This is the
// backend/GraphQL message BEFORE any display formatting or localization, so it
// is safe to match structural sentinel codes against (e.g. codes thrown by a
// Lambda as `new Error('SOME_CODE')`). Returns null when no raw string message
// can be determined.
export function getRawErrorMessage(e: unknown): string | null {
  if (e instanceof Error) {
    return e.message;
  }
  if (
    typeof e === 'object' &&
    e !== null &&
    'data' in e &&
    typeof e.data === 'object' &&
    e.data !== null &&
    'errors' in e &&
    Array.isArray(e.errors) &&
    e.errors.length === 1
  ) {
    // graphql error with 1 error
    return e.errors[0].message;
  }
  return null;
}

export function handleError(e: unknown, label?: string): BeemError {
  const rawMessage = getRawErrorMessage(e);
  // fallback to a serialized form when no raw string message is available
  const text = rawMessage !== null ? rawMessage : JSON.stringify(e);
  if (label) logger.error(text, { label });

  let details: string | undefined;
  if (typeof e === 'object' && e !== null && '__debugContext' in e) {
    details = String((e as Record<string, unknown>).__debugContext);
  }

  pushDebugError(text, details);

  return {
    formattedString: text,
    details,
    originalError: e,
  };
}

export function handleUserError(e: unknown, label: string): BeemError {
  return handleError(e, label);
}

export function handleSystemError(e: unknown, label: string): BeemError {
  Sentry.captureException(e);
  const errorResult = handleError(e, label);
  // Only track if mixpanel is loaded
  const isMixpanelLoaded = () => (mixpanel as unknown as { __loaded?: boolean }).__loaded;
  if (isMixpanelLoaded()) {
    mixpanel.track('Error', {
      error_type: 'system',
      error_message: errorResult.formattedString,
      error_code: label,
      page_url: window.location.href,
    });
  }
  return errorResult;
}

export function handleUnknownError(e: unknown): void {
  const details: string[] = [];
  if (typeof e === 'object' && e !== null) {
    if ('code' in e) details.push(`code=${String(e.code)}`);
    if ('message' in e) details.push(`message=${String(e.message)}`);
    if ('status' in e) details.push(`status=${String(e.status)}`);
    if ('detail' in e) details.push(`detail=${String(e.detail)}`);
    if ('details' in e) details.push(`details=${String(e.details)}`);
  }
  logger.error(`An unknown error occurred: ${details.join(', ')}`);
  logger.error(`Whole error object: ${String(e)}`);
}
